import getErrorCardTitle from './getErrorCardTitle';
import { ErrorCard } from './types';

export interface NumberedErrorCard<TError> {
    card: ErrorCard<TError>;
    /** The card's position in the whole list, counted from one. */
    number: number;
}

export interface ErrorCardSection<TError> {
    /** "Declaration", "Item N" or "Items 1, 3" - what every card in it is about. */
    title: string;
    cards: NumberedErrorCard<TError>[];
}

const SINGLE_ITEM_TITLE = /^Item (\d+)$/;

const DELETED_ITEM_TITLE = 'Deleted item';

/** Declaration first, then single items by number, then errors naming several items. */
const getSectionOrder = (title: string): [rank: number, itemNumber: number] => {
    if (title === 'Declaration') return [0, 0];

    const singleItemNumber = SINGLE_ITEM_TITLE.exec(title)?.[1];
    if (singleItemNumber) return [1, Number(singleItemNumber)];

    return [2, 0];
};

/**
 * The error cards in one section per item, so all the errors of an item are read together - the
 * way the products table counts them. A card goes to the section its title names, so an error
 * naming several items is a section of its own rather than listed twice.
 *
 * An error only about items deleted since the rejection is left out: there is nothing left to fix,
 * and the products table no longer counts it either.
 *
 * The numbers run across the sections, so they still add up to the total.
 */
const groupErrorCardsByItem = <TError>(cards: ErrorCard<TError>[]): ErrorCardSection<TError>[] => {
    const cardsByTitle = new Map<string, ErrorCard<TError>[]>();
    cards.forEach((card) => {
        const title = getErrorCardTitle(card.fields);
        if (title === DELETED_ITEM_TITLE) return;

        cardsByTitle.set(title, [...(cardsByTitle.get(title) ?? []), card]);
    });

    const sortedTitles = Array.from(cardsByTitle.keys()).sort((titleA, titleB) => {
        const [rankA, itemNumberA] = getSectionOrder(titleA);
        const [rankB, itemNumberB] = getSectionOrder(titleB);
        return rankA - rankB || itemNumberA - itemNumberB;
    });

    let number = 0;
    return sortedTitles.map((title) => ({
        title,
        cards: (cardsByTitle.get(title) ?? []).map((card) => {
            number += 1;
            return { card, number };
        }),
    }));
};

export default groupErrorCardsByItem;
